import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import {
  forceCenter, forceCollide, forceLink, forceManyBody, forceSimulation,
  type Simulation, type SimulationLinkDatum, type SimulationNodeDatum,
} from "d3-force";
import type { Graph, GraphEdge, GraphNode } from "../../services/cuitIntel";
import { edgeKey, edgeWidth, fmtArsShort, fmtCuit, nodeRadius } from "../../lib/cuitGraph";
import { BRAND } from "../../styles/theme";

/**
 * Grafo pagador ↔ collector con d3-force sobre SVG propio (sin canvas ni CDN).
 *  - pagador = círculo, collector = cuadrado redondeado; tamaño por monto
 *  - grosor de arista por cantidad de cobros (log)
 *  - hub (billetera/PSP) atenuado y con borde punteado: se muestra, no se expande
 *  - click selecciona, doble click expande, arrastrar fija el nodo; rueda = zoom
 */
interface SimNode extends SimulationNodeDatum { id: string; data: GraphNode; r: number }
interface SimLink extends SimulationLinkDatum<SimNode> { key: string; data: GraphEdge; w: number }

export interface GraphViewProps {
  graph:            Graph | null;
  rootId:           string | null;
  selectedId:       string | null;
  selectedEdgeKey:  string | null;
  loading?:         boolean;
  height?:          number;
  onSelectNode:     (n: GraphNode) => void;
  onSelectEdge:     (e: GraphEdge) => void;
  onExpandNode:     (n: GraphNode) => void;
}

const COLORS = {
  payer:     "#0EA5E9",
  payerHub:  "#CBD5E1",
  collector: BRAND.primary,
  edge:      "#94A3B8",
  same:      "#F59E0B",
  rootRing:  "#111827",
};

export default function GraphView({
  graph, rootId, selectedId, selectedEdgeKey, loading, height = 560,
  onSelectNode, onSelectEdge, onExpandNode,
}: GraphViewProps) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(900);
  const [, setFrame] = useState(0);
  const [view, setView] = useState({ x: 0, y: 0, k: 1 });
  const [hover, setHover] = useState<string | null>(null);
  const simRef = useRef<Simulation<SimNode, SimLink> | null>(null);
  const posRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  const drag = useRef<{ node?: SimNode; sx: number; sy: number; moved: boolean; pan?: { x: number; y: number } } | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(Math.max(320, el.clientWidth)));
    ro.observe(el);
    setWidth(Math.max(320, el.clientWidth));
    return () => ro.disconnect();
  }, []);

  const { nodes, links } = useMemo(() => {
    if (!graph) return { nodes: [] as SimNode[], links: [] as SimLink[], maxSum: 0, maxN: 0 };
    const maxSum = Math.max(0, ...graph.nodes.map((n) => Number(n.sum) || 0));
    const maxN = Math.max(0, ...graph.edges.map((e) => e.n ?? 0));
    const nodes: SimNode[] = graph.nodes.map((n) => {
      const prev = posRef.current.get(n.id);
      return { id: n.id, data: n, r: nodeRadius(Number(n.sum) || 0, maxSum), ...(prev ?? {}) };
    });
    const byId = new Map(nodes.map((n) => [n.id, n]));
    const links: SimLink[] = graph.edges
      .filter((e) => byId.has(e.source) && byId.has(e.target))
      .map((e) => ({ key: edgeKey(e), data: e, w: e.same_cuit ? 1.5 : edgeWidth(e.n ?? 0, maxN),
                     source: byId.get(e.source)!, target: byId.get(e.target)! }));
    return { nodes, links, maxSum, maxN };
  }, [graph]);

  useEffect(() => {
    simRef.current?.stop();
    if (!nodes.length) return;
    const root = nodes.find((n) => n.id === rootId);
    if (root && root.x == null) { root.x = width / 2; root.y = height / 2; }
    const sim = forceSimulation<SimNode>(nodes)
      .force("link", forceLink<SimNode, SimLink>(links).id((d) => d.id)
        .distance((l) => {
          const s = l.source as SimNode, t = l.target as SimNode;
          return 60 + (s.r ?? 0) + (t.r ?? 0) + (l.data.same_cuit ? 0 : 30);
        })
        .strength((l) => (l.data.same_cuit ? 1 : 0.5)))
      .force("charge", forceManyBody<SimNode>().strength((d) => -120 - d.r * 12))
      .force("collide", forceCollide<SimNode>().radius((d) => d.r + 6).iterations(2))
      .force("center", forceCenter(width / 2, height / 2).strength(0.05))
      .alpha(1).alphaDecay(0.035);
    sim.on("tick", () => {
      for (const n of nodes) if (n.x != null && n.y != null) posRef.current.set(n.id, { x: n.x, y: n.y });
      if (rafRef.current == null) rafRef.current = requestAnimationFrame(() => { rafRef.current = null; setFrame((f) => f + 1); });
    });
    simRef.current = sim;
    return () => { sim.stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, links, width, height]);

  // ---- interacción ----
  function toLocal(ev: React.PointerEvent | React.WheelEvent): { x: number; y: number } {
    const rect = (ev.currentTarget as SVGSVGElement).getBoundingClientRect();
    return { x: (ev.clientX - rect.left - view.x) / view.k, y: (ev.clientY - rect.top - view.y) / view.k };
  }

  function onWheel(ev: React.WheelEvent<SVGSVGElement>) {
    ev.preventDefault();
    const rect = ev.currentTarget.getBoundingClientRect();
    const px = ev.clientX - rect.left, py = ev.clientY - rect.top;
    const k = Math.min(4, Math.max(0.3, view.k * (ev.deltaY < 0 ? 1.12 : 0.89)));
    setView({ k, x: px - ((px - view.x) / view.k) * k, y: py - ((py - view.y) / view.k) * k });
  }

  function onBgDown(ev: React.PointerEvent<SVGSVGElement>) {
    if (ev.button !== 0) return;
    drag.current = { sx: ev.clientX, sy: ev.clientY, moved: false, pan: { x: view.x, y: view.y } };
    (ev.currentTarget as Element).setPointerCapture(ev.pointerId);
  }

  function onNodeDown(ev: React.PointerEvent, n: SimNode) {
    if (ev.button !== 0) return;
    ev.stopPropagation();
    drag.current = { node: n, sx: ev.clientX, sy: ev.clientY, moved: false };
    n.fx = n.x; n.fy = n.y;
    simRef.current?.alphaTarget(0.25).restart();
    (ev.currentTarget as Element).setPointerCapture(ev.pointerId);
  }

  function onMove(ev: React.PointerEvent<SVGSVGElement>) {
    const d = drag.current;
    if (!d) return;
    const dx = ev.clientX - d.sx, dy = ev.clientY - d.sy;
    if (Math.abs(dx) + Math.abs(dy) > 4) d.moved = true;
    if (d.node) {
      const p = toLocal(ev);
      d.node.fx = p.x; d.node.fy = p.y;
    } else if (d.pan) {
      setView((v) => ({ ...v, x: d.pan!.x + dx, y: d.pan!.y + dy }));
    }
  }

  function onUp(ev: React.PointerEvent<SVGSVGElement>) {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    if (d.node) {
      simRef.current?.alphaTarget(0);
      if (!d.moved) { d.node.fx = null; d.node.fy = null; onSelectNode(d.node.data); }
    }
    void ev;
  }

  const showAllLabels = nodes.length <= 70;
  const neighborsOfSelected = useMemo(() => {
    const s = new Set<string>();
    if (!selectedId) return s;
    for (const l of links) {
      const a = (l.source as SimNode).id, b = (l.target as SimNode).id;
      if (a === selectedId) s.add(b);
      if (b === selectedId) s.add(a);
    }
    return s;
  }, [links, selectedId]);

  return (
    <Box ref={boxRef} sx={{ position: "relative", width: "100%", height, border: "1px solid", borderColor: "divider",
                            borderRadius: 2, overflow: "hidden", bgcolor: "#FBFBFD" }}>
      {!graph && !loading && (
        <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", color: "text.secondary", p: 3, textAlign: "center" }}>
          <Typography variant="body2">
            Buscá un CUIT, un collector, un id de Coelsa o un monto para ver el grafo de quién le paga a quién.
          </Typography>
        </Box>
      )}
      <svg width={width} height={height} style={{ display: "block", cursor: drag.current?.pan ? "grabbing" : "grab", touchAction: "none" }}
        onWheel={onWheel} onPointerDown={onBgDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp}>
        <g transform={`translate(${view.x},${view.y}) scale(${view.k})`}>
          {links.map((l) => {
            const s = l.source as SimNode, t = l.target as SimNode;
            if (s.x == null || t.x == null) return null;
            const sel = selectedEdgeKey === l.key;
            const dim = selectedId && !(s.id === selectedId || t.id === selectedId);
            return (
              <g key={l.key}>
                <line x1={s.x} y1={s.y} x2={t.x} y2={t.y} stroke="transparent" strokeWidth={Math.max(12, l.w + 10)}
                  style={{ cursor: "pointer" }} onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); if (!l.data.same_cuit) onSelectEdge(l.data); }}>
                  <title>{l.data.same_cuit ? "Mismo CUIT: es pagador y collector" :
                    `${l.data.n ?? 0} cobros · ${fmtArsShort(l.data.sum)}${l.data.nr ? ` · ${l.data.nr} rechazados` : ""}`}</title>
                </line>
                <line x1={s.x} y1={s.y} x2={t.x} y2={t.y}
                  stroke={l.data.same_cuit ? COLORS.same : sel ? COLORS.rootRing : COLORS.edge}
                  strokeWidth={sel ? l.w + 2 : l.w} strokeOpacity={dim ? 0.18 : 0.75}
                  strokeDasharray={l.data.same_cuit ? "4 3" : undefined} style={{ pointerEvents: "none" }} />
              </g>
            );
          })}
          {nodes.map((n) => {
            if (n.x == null || n.y == null) return null;
            const d = n.data;
            const isRoot = n.id === rootId, isSel = n.id === selectedId, isHover = n.id === hover;
            const dim = selectedId && !isSel && !neighborsOfSelected.has(n.id);
            const fill = d.type === "collector" ? COLORS.collector : d.is_hub ? COLORS.payerHub : COLORS.payer;
            const label = d.type === "collector" ? d.label : (d.label && d.label !== d.cuit ? d.label : fmtCuit(d.cuit));
            const showLabel = showAllLabels || d.type === "collector" || isRoot || isSel || isHover || neighborsOfSelected.has(n.id);
            const title = d.type === "collector"
              ? `${d.collector}${d.label !== d.collector ? ` — ${d.label}` : ""}\nCUIT ${fmtCuit(d.tax_id)}\n${d.n} cobros · ${fmtArsShort(d.sum)}`
              : `${d.label}\nCUIT ${fmtCuit(d.cuit)}${d.dv_ok === false ? " (DV inválido)" : ""}\n${d.n} cobros · ${fmtArsShort(d.sum)} · ${d.n_collectors ?? 0} collectors${d.is_hub ? "\nHUB (billetera/PSP): no se expande" : ""}`;
            return (
              <g key={n.id} transform={`translate(${n.x},${n.y})`} opacity={dim ? 0.3 : 1} style={{ cursor: "pointer" }}
                onPointerDown={(e) => onNodeDown(e, n)} onDoubleClick={(e) => { e.stopPropagation(); if (!d.is_hub) onExpandNode(d); }}
                onPointerEnter={() => setHover(n.id)} onPointerLeave={() => setHover(null)}>
                <title>{title}</title>
                {(isRoot || isSel) && (
                  d.type === "collector"
                    ? <rect x={-n.r - 4} y={-n.r - 4} width={2 * n.r + 8} height={2 * n.r + 8} rx={6} fill="none" stroke={COLORS.rootRing} strokeWidth={isSel ? 3 : 1.5} strokeDasharray={isRoot && !isSel ? "3 2" : undefined} />
                    : <circle r={n.r + 4} fill="none" stroke={COLORS.rootRing} strokeWidth={isSel ? 3 : 1.5} strokeDasharray={isRoot && !isSel ? "3 2" : undefined} />
                )}
                {d.type === "collector"
                  ? <rect x={-n.r} y={-n.r} width={2 * n.r} height={2 * n.r} rx={4} fill={fill} stroke="#fff" strokeWidth={1.5} />
                  : <circle r={n.r} fill={fill} stroke={d.is_hub ? "#64748B" : "#fff"} strokeWidth={1.5} strokeDasharray={d.is_hub ? "3 2" : undefined} />}
                {showLabel && (
                  <text y={n.r + 12} textAnchor="middle" fontSize={11} fontWeight={isRoot || isSel ? 700 : 500}
                    fill="#1F2937" stroke="#FBFBFD" strokeWidth={3} paintOrder="stroke" style={{ pointerEvents: "none", userSelect: "none" }}>
                    {label.length > 26 ? label.slice(0, 25) + "…" : label}
                  </text>
                )}
              </g>
            );
          })}
        </g>
      </svg>
      <Box sx={{ position: "absolute", left: 10, bottom: 8, display: "flex", gap: 2, fontSize: 11, color: "text.secondary", alignItems: "center", pointerEvents: "none" }}>
        <span><svg width={12} height={12}><circle cx={6} cy={6} r={5} fill={COLORS.payer} /></svg> pagador</span>
        <span><svg width={12} height={12}><rect width={12} height={12} rx={2} fill={COLORS.collector} /></svg> collector</span>
        <span><svg width={12} height={12}><circle cx={6} cy={6} r={5} fill={COLORS.payerHub} stroke="#64748B" strokeDasharray="2 1" /></svg> hub (no se expande)</span>
        <span>tamaño = monto · grosor = cobros · doble click expande</span>
      </Box>
      {graph?.truncated && (
        <Box sx={{ position: "absolute", right: 10, top: 8, fontSize: 11, bgcolor: "#FEF3C7", color: "#92400E", px: 1, py: 0.25, borderRadius: 1 }}>
          Grafo recortado: no se muestran todos los vecinos
        </Box>
      )}
      {loading && (
        <Box sx={{ position: "absolute", right: 10, bottom: 8, fontSize: 11, color: "text.secondary" }}>Cargando…</Box>
      )}
    </Box>
  );
}
